import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormControlLabel, Typography } from '@mui/material';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  AutocompleteOptions,
  Button,
  SelectFieldWrapper,
  Checkbox,
} from '@Components/index';
import Modal from '@Components/Modal';
import Radio from '@Components/Radio/Radio.component';
import { StyledRadioGroup } from '@Components/ReportConfig/ReportConfig.styles';
import commonConstants from '@Constants/common.constants';
import { VoiceProvider } from '@Models/callerIds.models';
import {
  useBuyPhoneNumberMutation,
  useGetAreaCodeListQuery,
  useGetAvailablePhoneNumbersMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  RotationSystemNumbersModalInputs,
  RotationSystemNumbersModalProps,
} from './AbstractSystemNumbersModal.types';

const AbstractRotationSystemNumbersModal: FunctionComponent<RotationSystemNumbersModalProps> =
  (props: RotationSystemNumbersModalProps): JSX.Element => {
    const { userId, isOpen, setIsOpen, availableLimitToBuy, provider } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();

    const { supportStirShaken } = props;

    const [isStirShakenChecked, setIsStirShakenChecked] = useState(false);

    const phoneAmountOptions = Array.from(
      { length: availableLimitToBuy },
      (v, k) => k + 1
    );
    const [phoneAmount, setPhoneAmount] = useState<number>(1);
    const [availableTwilioNumbers, setAvailableTwilioNumbers] =
      useState<number>(0);
    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });

    // get list of all area codes
    const { data: dataOfAreaCodeList = [] } = useGetAreaCodeListQuery();

    // load available phone numbers by area code
    const [availableList, availableListMethods] =
      useGetAvailablePhoneNumbersMutation();

    // buy phone number
    const [buyPhoneNumber, buyPhoneNumberMethods] = useBuyPhoneNumberMutation();

    const areaCodes = dataOfAreaCodeList.map((code) => ({
      label: `+1 (${code.areaCode})`,
      value: code.areaCode,
    }));

    const required = t('common.formErrorMessages.required');
    const methods = useForm<RotationSystemNumbersModalInputs>({
      resolver: yupResolver(
        yup.object({
          areaCode: yup
            .object()
            .nullable()
            .required(required)
            .shape({ value: yup.string(), label: yup.string() }),
        })
      ),
      mode: 'onChange',
      shouldUnregister: true,
    });

    /**
     * Buy Twilio phone number Form Methods: Updates the form state on submit
     */
    const onSubmitHandler: SubmitHandler<RotationSystemNumbersModalInputs> = (
      formData
    ) => {
      if (formData) {
        return buyPhoneNumber({
          userId: userId?.toString() || '',
          areaCode: formData.areaCode.value,
          phoneAmount: phoneAmount.toString(),
          stirShaken: isStirShakenChecked,
        });
      }
    };

    /**
     * Error and Success alerts for buying twilio phone number
     */
    useEffect(() => {
      if (buyPhoneNumberMethods.error) {
        triggerAlert(buyPhoneNumberMethods.error, setAlertState);
      }
      if (buyPhoneNumberMethods.isSuccess) {
        setIsOpen(!buyPhoneNumberMethods.data.boughtPhones);
      }
    }, [
      buyPhoneNumberMethods.isSuccess,
      buyPhoneNumberMethods.error,
      buyPhoneNumberMethods.data,
      triggerAlert,
      buyPhoneNumberMethods,
      setIsOpen,
    ]);

    /** Handles Phone Type Radio Button Group Change */
    const handlePhonesAmountChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setPhoneAmount(parseInt((event.target as HTMLInputElement).value));
    };

    const areaCodeChangeHandler = (
      _event: React.SyntheticEvent<Element, Event>,
      value: AutocompleteOptions
    ) => {
      const selectedAreaCode = value.value;
      if (selectedAreaCode) {
        return availableList({
          areaCode: selectedAreaCode,
          limit: availableLimitToBuy.toString(),
          userId: userId?.toString() || '',
        });
      }
    };

    useEffect(() => {
      if (availableListMethods.isSuccess) {
        if (availableListMethods.data.length > 0) {
          setAvailableTwilioNumbers(availableListMethods.data.length);
        } else {
          setAvailableTwilioNumbers(0);
          triggerAlert(
            commonConstants.GLOBAL_MESSAGES.UNAVAILABLE_ROTATION_NUMBER,
            setAlertState,
            'error'
          );
        }
      }
    }, [availableListMethods, triggerAlert]);

    const closeModal = () => {
      setIsOpen(false);
      setAvailableTwilioNumbers(0);
    };

    return (
      <>
        <Modal
          open={isOpen}
          headerText={t('userCallerIds.Add System Phone')}
          onClose={() => closeModal()}
          alertState={alertState}
        >
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  sx={{ padding: '2rem 2.4rem 2.4rem' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={3.8}
                  >
                    <Typography variant="h2" mr={2}>
                      {`${t('userCallerIds.Area Code')}:`}
                    </Typography>
                    <SelectFieldWrapper
                      name="areaCode"
                      options={areaCodes}
                      multiple={false}
                      textFieldProps={{
                        placeholder: t('userCallerIds.Select Area Code'),
                      }}
                      onChange={areaCodeChangeHandler}
                      disableClearable
                      sx={{
                        minWidth: '18.6rem',
                        maxWidth: '18.6rem',
                      }}
                    />
                  </Box>
                  {availableTwilioNumbers !== 0 &&
                    availableLimitToBuy > availableTwilioNumbers && (
                      <Box pt={2} px={11.5}>
                        <Typography variant="main" textAlign="center">
                          <Trans
                            i18nKey="userCallerIds.System Numbers Message"
                            components={{ strong: <strong /> }}
                            values={{ amount: availableTwilioNumbers }}
                          />
                        </Typography>
                      </Box>
                    )}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt={3}
                  >
                    <Typography variant="h2" mb={1}>
                      {t('userCallerIds.Amount of phones')}
                    </Typography>
                    <StyledRadioGroup
                      row
                      sx={{ justifyContent: 'center', gap: '5px' }}
                      aria-label="phone-type"
                      name="phone-type-radio-group"
                      value={phoneAmount}
                      onChange={handlePhonesAmountChange}
                    >
                      {phoneAmountOptions.map(
                        (item: number, idx: number): ReactNode => (
                          <FormControlLabel
                            key={idx}
                            value={phoneAmountOptions[idx]}
                            control={<Radio />}
                            label={item}
                            disabled={item > availableTwilioNumbers}
                          />
                        )
                      )}
                    </StyledRadioGroup>
                  </Box>
                  <Box py={4} px={11.5}>
                    <Typography variant="main" textAlign="center">
                      {t('userCallerIds.Modal Message')}
                    </Typography>
                  </Box>

                  {provider === VoiceProvider.TWILIO && (
                    <FormControlLabel
                      title={
                        !supportStirShaken
                          ? t('common.messages.STIR-SHAKEN-NOT-SUPPORT')
                          : ''
                      }
                      control={
                        <Checkbox
                          checked={isStirShakenChecked}
                          disabled={!supportStirShaken}
                          onChange={() => {
                            setIsStirShakenChecked(!isStirShakenChecked);
                          }}
                          color="secondary"
                        />
                      }
                      label={
                        <>
                          <Typography variant="main">
                            {t('common.messages.STIR-SHAKEN')}
                          </Typography>
                        </>
                      }
                    />
                  )}
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    borderTop: '0.1rem solid #A3ABB2',
                    padding: '3rem 2.4rem 2.4rem',
                  }}
                >
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      onClick={() => closeModal()}
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 'auto', mr: 1.6 }}
                    >
                      {t('common.messages.Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={availableTwilioNumbers < phoneAmount}
                    >
                      {t('common.messages.Submit')}
                    </Button>
                  </Box>
                </Box>
              </>
            </Box>
          </FormProvider>
        </Modal>
      </>
    );
  };

export default AbstractRotationSystemNumbersModal;
